/**
 * components/accounting/DashboardCards.tsx
 * Dashboard summary cards for quick KPIs with Arabic tooltips and Gregorian dates.
 * Fees removed. Adds expenses insights and net for next 7 days.
 */

import { useMemo } from 'react'
import { formatCurrency } from '../../lib/format'
import { CreditCard, Banknote, Wallet, CalendarDays, Receipt } from 'lucide-react'
import { addDays, isAfter, isBefore, startOfDay } from 'date-fns'
import { useUserStore } from '@/store/user'

/**
 * StatCard
 * Small reusable stat card component with optional hover hint (title attribute).
 */
function StatCard(props: { title: string; value: string; icon: React.ReactNode; accent?: string; hint?: string }) {
  const { title, value, icon, accent = 'bg-indigo-600', hint } = props
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" title={hint} aria-label={hint ? `${title}: ${hint}` : title}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="mt-2 text-lg font-semibold text-slate-900">{value}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg text-white ${accent}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

/**
 * DashboardCards
 * Renders KPIs: total sales, pending deposits, balance, pending expenses, net next 7 days.
 * Adds tooltips and uses memoized local derivation to avoid unnecessary updates.
 */
export function DashboardCards() {
  // Subscribe only to raw slices
  const transactions = useUserStore((s) => s.transactions)
  const expenses = useUserStore((s) => s.expenses)
  const calculateStoreBalance = useUserStore((s) => s.getStoreBalance)


  const currentBalance = useMemo(() => {
    return calculateStoreBalance()
  }, [transactions, expenses])
  
  // Derive summary locally and memoized by slices
  const kpis = useMemo(() => {
    const totalSales = transactions.reduce((s, t) => s + t.amount_gross, 0)
    const pendingDeposits = transactions.filter((t) => !t.deposited).reduce((s, t) => s + t.net_amount, 0)

    const today = startOfDay(new Date())
    const seven = startOfDay(addDays(today, 7))
    const upcomingDeposits7 = transactions
      .filter(
        (t) =>
          !t.deposited &&
          !isBefore(new Date(t.deposit_date), today) &&
          !isAfter(new Date(t.deposit_date), seven)
      )
      .reduce((s, t) => s + t.net_amount, 0)

    const pendingExpenses = expenses.filter((e) => !e.paid).reduce((s, e) => s + e.amount, 0)
    const upcomingExpenses7 = expenses
      .filter(
        (e) =>
          !e.paid &&
          !isBefore(new Date(e.payment_date), today) &&
          !isAfter(new Date(e.payment_date), seven)
      )
      .reduce((s, e) => s + e.amount, 0)

    const netNext7 = upcomingDeposits7 - upcomingExpenses7

    return { totalSales, pendingDeposits, pendingExpenses, currentBalance, netNext7 }
  }, [transactions, expenses])

  const cards = [
    {
      title: 'إجمالي المبيعات',
      value: formatCurrency(kpis.totalSales),
      icon: <CreditCard className="h-5 w-5" />,
      accent: 'bg-indigo-600',
      hint: 'إجمالي جميع عمليات البيع المُسجّلة.',
    },
    {
      title: 'الصافي المعلّق',
      value: formatCurrency(kpis.pendingDeposits),
      icon: <Banknote className="h-5 w-5" />,
      accent: 'bg-amber-600',
      hint: 'إجمالي المبالغ التي ستُودَع لاحقًا ولم تُسجَّل في الرصيد بعد.',
    },
    {
      title: 'رصيد الجاري',
      value: formatCurrency(kpis.currentBalance),
      icon: <Wallet className="h-5 w-5" />,
      accent: 'bg-emerald-600',
      hint: 'رصيد الحساب البنكي الحالي داخل النظام بعد القيود المنفّذة.',
    },
    {
      title: 'المصروفات المعلّقة',
      value: formatCurrency(kpis.pendingExpenses),
      icon: <Receipt className="h-5 w-5" />,
      accent: 'bg-rose-600',
      hint: 'إجمالي المصروفات المجدوَلة التي لم تُدفع بعد.',
    },
    {
      title: 'صافي 7 أيام',
      value: formatCurrency(kpis.netNext7),
      icon: <CalendarDays className="h-5 w-5" />,
      accent: 'bg-sky-600',
      hint: 'المبالغ المتوقّع دخولها ناقص المصروفات المتوقّع دفعها خلال 7 أيام.',
    },
  ]

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {cards.map((c) => (
        <StatCard key={c.title} title={c.title} value={c.value} icon={c.icon} accent={c.accent} hint={c.hint} />
      ))}
    </div>
  )
}
